import {
    createClause,
    getClauseTypes,
    getPolicyTypes,
    getTopics,
    IApiAxiosError,
    IClauseCreate,
    IClauseExample,
} from "@/api";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Badge } from "@/components/ui/badge";
import {
    CLAUSE_TYPE_QUERY_FILTER,
    POLICY_TYPE_QUERY_FILTER,
    TOPIC_QUERY_FILTER,
} from "@/config/constants";
import { Add, Remove } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { ActionButton } from "@/components/shared/buttons/ActionButton";

interface NewClauseFormValues {
    topic_ids: number[];
    policy_type_id: number | null;
    clause_type_ids: number[];
    title: string;
    description: string;
    guidance: string;
    examples: IClauseExample[];
}

interface NewClauseDialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    initialExamples?: string[];
    onCreated?: (clauseId: number) => void;
}

export const NewClauseDialog = ({
    open,
    onClose,
    title: prefillTitle,
    description: prefillDescription,
    initialExamples,
    onCreated,
}: NewClauseDialogProps) => {
    const queryClient = useQueryClient();

    const prefillExamples: IClauseExample[] = initialExamples?.length
        ? initialExamples.map((text) => ({ id: -1, text }))
        : [{ id: -1, text: "" }];

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<NewClauseFormValues>({
        defaultValues: {
            topic_ids: [],
            policy_type_id: null,
            clause_type_ids: [],
            title: prefillTitle || "",
            description: prefillDescription || "",
            guidance: "",
            examples: prefillExamples,
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "examples",
    });

    // Track which example rows were pre-filled by the AI gap-suggestion flow.
    // Keyed on react-hook-form's stable `field.id`, NOT array index; `remove`
    // shifts indexes but preserves field IDs. The badge clears on first edit.
    const [aiSuggestedFieldIds, setAiSuggestedFieldIds] = useState<Set<string>>(
        () =>
            initialExamples?.length
                ? new Set(fields.map((f) => f.id))
                : new Set()
    );

    const clearAiSuggestion = (fieldId: string) => {
        setAiSuggestedFieldIds((prev) => {
            if (!prev.has(fieldId)) return prev;
            const next = new Set(prev);
            next.delete(fieldId);
            return next;
        });
    };

    // --- Data queries ---
    const { data: topicsData, isPending: topicsLoading } = useQuery({
        queryKey: ["topics-autocomplete", TOPIC_QUERY_FILTER],
        queryFn: () => getTopics(1, 100, TOPIC_QUERY_FILTER),
    });

    const { data: policyTypesData, isPending: policyTypesLoading } = useQuery({
        queryKey: ["policyTypes"],
        queryFn: () => getPolicyTypes(1, 100, POLICY_TYPE_QUERY_FILTER),
    });

    const { data: clauseTypesData, isPending: clauseTypesLoading } = useQuery({
        queryKey: ["clauseTypes"],
        queryFn: () => getClauseTypes(1, 100, CLAUSE_TYPE_QUERY_FILTER),
    });

    const topicOptions = topicsData?.items ?? [];
    const policyTypeOptions = policyTypesData?.items ?? [];
    const clauseTypeOptions = clauseTypesData?.items ?? [];

    // --- Mutation ---
    const createMutation = useMutation({
        mutationFn: (data: IClauseCreate) => createClause(data),
        onSuccess: (newClause) => {
            queryClient.invalidateQueries({ queryKey: ["admin-clauses"] });
            onCreated?.(newClause.id);
            handleClose();
        },
    });

    const handleClose = () => {
        reset();
        createMutation.reset();
        onClose();
    };

    const onSubmit = (data: NewClauseFormValues) => {
        createMutation.mutate({
            title: data.title.trim(),
            description: data.description.trim(),
            guidance: data.guidance.trim(),
            examples: data.examples.map((ex) => ex.text.trim()),
            policy_type_id: data.policy_type_id!,
            clause_type_ids: data.clause_type_ids,
            topic_ids: data.topic_ids,
        });
    };

    const axiosError = createMutation.error as IApiAxiosError | null;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="New Clause"
            subtitle="Please provide the following information."
            isLoading={createMutation.isPending}
            maxWidth="sm"
            actions={[
                {
                    label: "Save Clause",
                    onClick: handleSubmit(onSubmit),
                    variant: "contained",
                    color: "primary",
                    disabled: createMutation.isPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data?.detail ||
                            "An error occurred while creating the clause."}
                    </Typography>
                )
            }
        >
            <Stack spacing={2.5}>
                {/* Topics (required, multi-select) */}
                <Controller
                    name="topic_ids"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.length > 0 ||
                            "At least one topic is required",
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                            multiple
                            options={topicOptions}
                            value={topicOptions.filter((t) =>
                                value.includes(t.id)
                            )}
                            getOptionLabel={(option) => option.name}
                            onChange={(_, newValue) =>
                                onChange(newValue.map((t) => t.id))
                            }
                            loading={topicsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={ref}
                                    label="Topics"
                                    required
                                    error={!!errors.topic_ids}
                                    helperText={errors.topic_ids?.message}
                                />
                            )}
                            size="small"
                            filterSelectedOptions
                            noOptionsText={
                                topicsLoading ? "Loading..." : "No topics found"
                            }
                        />
                    )}
                />

                {/* Policy Type (required, single select) */}
                <Controller
                    name="policy_type_id"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value !== null || "Policy type is required",
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                            options={policyTypeOptions}
                            value={
                                policyTypeOptions.find((p) => p.id === value) ??
                                null
                            }
                            getOptionLabel={(option) =>
                                `${option.name} - ${option.short_name}`
                            }
                            onChange={(_, newValue) =>
                                onChange(newValue?.id ?? null)
                            }
                            loading={policyTypesLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={ref}
                                    label="Policy Type"
                                    required
                                    error={!!errors.policy_type_id}
                                    helperText={errors.policy_type_id?.message}
                                />
                            )}
                            size="small"
                            noOptionsText={
                                policyTypesLoading
                                    ? "Loading..."
                                    : "No policy types found"
                            }
                        />
                    )}
                />

                {/* Clause Types (required, multi-select) */}
                <Controller
                    name="clause_type_ids"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.length > 0 ||
                            "At least one clause type is required",
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                            multiple
                            options={clauseTypeOptions}
                            value={clauseTypeOptions.filter((ct) =>
                                value.includes(ct.id)
                            )}
                            getOptionLabel={(option) => option.name}
                            onChange={(_, newValue) =>
                                onChange(newValue.map((ct) => ct.id))
                            }
                            loading={clauseTypesLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={ref}
                                    label="Clause Types"
                                    required
                                    error={!!errors.clause_type_ids}
                                    helperText={errors.clause_type_ids?.message}
                                />
                            )}
                            size="small"
                            filterSelectedOptions
                            noOptionsText={
                                clauseTypesLoading
                                    ? "Loading..."
                                    : "No clause types found"
                            }
                        />
                    )}
                />

                {/* Title */}
                <Controller
                    name="title"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.trim() !== "" || "Title is required",
                    }}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Clause Title"
                            required
                            error={!!errors.title}
                            helperText={errors.title?.message}
                            sx={{ width: "100%" }}
                        />
                    )}
                />

                {/* Description */}
                <Controller
                    name="description"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.trim() !== "" || "Description is required",
                    }}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Description"
                            required
                            error={!!errors.description}
                            helperText={errors.description?.message}
                            sx={{ width: "100%" }}
                        />
                    )}
                />

                {/* Guidance */}
                <Controller
                    name="guidance"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.trim() !== "" || "Guidance is required",
                    }}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Guidance"
                            required
                            multiline
                            minRows={3}
                            error={!!errors.guidance}
                            helperText={errors.guidance?.message}
                            sx={{ width: "100%" }}
                        />
                    )}
                />

                {/* Examples (dynamic array) */}
                {fields.map((field, index) => {
                    const isAiSuggested = aiSuggestedFieldIds.has(field.id);

                    return (
                        <Stack
                            key={field.id}
                            direction="row"
                            alignItems="center"
                            gap={1}
                        >
                            <Box sx={{ position: "relative", flex: 1 }}>
                                <Controller
                                    name={`examples.${index}.text`}
                                    control={control}
                                    rules={{
                                        validate: (value) =>
                                            value.trim() !== "" ||
                                            "Example text is required",
                                    }}
                                    render={({
                                        field: { ref, onChange, ...f },
                                    }) => (
                                        <TextField
                                            {...f}
                                            inputRef={ref}
                                            onChange={(e) => {
                                                onChange(e);
                                                clearAiSuggestion(field.id);
                                            }}
                                            label="Example Text"
                                            required
                                            multiline
                                            minRows={2}
                                            error={
                                                !!errors.examples?.[index]?.text
                                            }
                                            helperText={
                                                errors.examples?.[index]?.text
                                                    ?.message
                                            }
                                            sx={{ width: "100%" }}
                                        />
                                    )}
                                />
                                {isAiSuggested && (
                                    <Badge
                                        variant="outline"
                                        className="border-copper/40 bg-popover text-copper pointer-events-none absolute top-0 right-3 z-10 h-4 -translate-y-1/2 gap-1 px-1.5 text-[10px] font-semibold tracking-wider uppercase [&>svg]:size-2.5!"
                                    >
                                        <Sparkles aria-hidden />
                                        AI suggested
                                    </Badge>
                                )}
                            </Box>
                            {fields.length > 1 && (
                                <IconButton
                                    onClick={() => {
                                        remove(index);
                                        clearAiSuggestion(field.id);
                                    }}
                                    aria-label="Remove example text"
                                    size="small"
                                >
                                    <Remove />
                                </IconButton>
                            )}
                        </Stack>
                    );
                })}

                <Box>
                    <ActionButton
                        variant="outlined"
                        size="small"
                        startIcon={<Add fontSize="small" />}
                        onClick={() =>
                            append({ id: -1, text: "" } as IClauseExample)
                        }
                    >
                        Add Example
                    </ActionButton>
                </Box>
            </Stack>
        </Modal>
    );
};
